/**
 * CoursePublish — course-level export stage.
 * Shows lesson readiness, lets user export compiled lessons as a ZIP.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useCourseContext } from '../../CourseContext';
import { courseRunsService } from '../../../../services/courseRunsService';

interface LessonStatus {
  run_id: string;
  lesson_number: number;
  title: string;
  status: string;
  compiled: boolean;
}

interface ExportResult {
  download_url: string;
  zip_size_bytes: number;
  included_lessons: Array<{ lesson_number: number; title: string }>;
  skipped_lessons: Array<{ lesson_number: number; title: string; reason: string }>;
}

export function CoursePublish() {
  const { run } = useCourseContext();
  const courseRunId = run?.run_id;

  const [lessons, setLessons] = useState<LessonStatus[]>([]);
  const [total, setTotal] = useState(0);
  const [compiledCount, setCompiledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!courseRunId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await courseRunsService.getExportStatus(courseRunId);
      setLessons(data.lessons);
      setTotal(data.total);
      setCompiledCount(data.compiled_count);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load export status');
    } finally {
      setLoading(false);
    }
  }, [courseRunId]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const handleExport = useCallback(async () => {
    if (!courseRunId) return;
    setExporting(true);
    setExportError(null);
    setExportResult(null);
    try {
      const result = await courseRunsService.exportCourse(courseRunId);
      setExportResult(result);
    } catch (e: unknown) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [courseRunId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-talaera-blue" />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{error}</div>;
  }

  if (total === 0) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 rounded p-4">
        No lessons found. Lock the syllabus to create lesson runs first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Course Export</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {compiledCount} of {total} lessons compiled and ready to export
          </p>
        </div>
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium rounded-md text-white disabled:opacity-50"
          style={{ backgroundColor: compiledCount > 0 ? '#1a2332' : '#9ca3af' }}
          disabled={compiledCount === 0 || exporting}
          onClick={handleExport}
        >
          {exporting ? 'Exporting...' : `Export Course (${compiledCount} lessons)`}
        </button>
      </div>

      {/* Warning if not all compiled */}
      {compiledCount > 0 && compiledCount < total && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {total - compiledCount} lesson{total - compiledCount > 1 ? 's are' : ' is'} not yet compiled.
          You can export now with the {compiledCount} compiled lesson{compiledCount > 1 ? 's' : ''}, or compile the remaining lessons first.
        </div>
      )}

      {/* Lesson readiness table */}
      <div className="border rounded overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-left text-gray-500 uppercase tracking-wider">
              <th className="px-3 py-2 font-semibold">#</th>
              <th className="px-3 py-2 font-semibold">Lesson</th>
              <th className="px-3 py-2 font-semibold">Pipeline</th>
              <th className="px-3 py-2 font-semibold">Compiled</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {lessons.map((lesson) => (
              <tr key={lesson.run_id} className={lesson.compiled ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-3 py-2 text-gray-600 font-mono">{lesson.lesson_number}</td>
                <td className="px-3 py-2 text-gray-800 font-medium">{lesson.title}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={lesson.status} />
                </td>
                <td className="px-3 py-2">
                  {lesson.compiled ? (
                    <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Ready
                    </span>
                  ) : (
                    <span className="text-gray-400">Not compiled</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Export error */}
      {exportError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{exportError}</div>
      )}

      {/* Export result */}
      {exportResult && (
        <div className="border border-green-200 bg-green-50 rounded p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-green-800">Export Complete</div>
              <div className="text-xs text-green-700 mt-0.5">
                {exportResult.included_lessons.length} lessons exported ({(exportResult.zip_size_bytes / 1024).toFixed(0)} KB)
              </div>
            </div>
            <a
              href={exportResult.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium rounded-md bg-green-700 text-white hover:bg-green-800"
            >
              Download ZIP
            </a>
          </div>

          {exportResult.included_lessons.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase text-green-600 mb-1">Included</div>
              <div className="text-xs text-green-800 space-y-0.5">
                {exportResult.included_lessons.map((l) => (
                  <div key={l.lesson_number}>Lesson {l.lesson_number}: {l.title}</div>
                ))}
              </div>
            </div>
          )}

          {exportResult.skipped_lessons.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase text-amber-600 mb-1">Skipped</div>
              <div className="text-xs text-amber-800 space-y-0.5">
                {exportResult.skipped_lessons.map((l) => (
                  <div key={l.lesson_number}>Lesson {l.lesson_number}: {l.title} ({l.reason})</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'not_started': 'bg-gray-100 text-gray-600',
    'in_progress': 'bg-blue-100 text-blue-700',
    'completed': 'bg-green-100 text-green-700',
  };
  const cls = styles[status] || 'bg-gray-100 text-gray-600';
  const label = status === 'not_started' ? 'Not started' : status.replace(/_/g, ' ');

  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${cls}`}>
      {label}
    </span>
  );
}
